// Home de "Meu Projeto" — Experience Wave 1.
// Reorganiza a experiência em torno do que já foi construído, da próxima
// decisão e do resultado que ela entrega. O cronograma detalhado passa a
// ser um recurso secundário (colapsado por padrão).
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  getActiveProject,
  completeTask,
  uncompleteTask,
  getUserProjects,
  listAchievements,
  deleteProject,
} from "@/lib/journey.functions";
import { getBusinessWorkspace } from "@/lib/workspace.functions";
import {
  computeStageProgress,
  computeProjectProgress,
  deriveMissionState,
  isExecutable,
  resolveResumeMission,
  type MissionState,
  type MissionStateRecord,
} from "@/lib/mission-status";
import {
  BLOCKS,
  BLOCK_BY_KEY,
  hasBlockContent,
  blockFieldRows,
  type BlockKey,
} from "@/lib/business-blocks";
import { summarizeDeliverables, STATUS_STYLES } from "@/lib/deliverables";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { track } from "@/hooks/useAnalytics";
import {
  ArrowRight,
  CheckCircle2,
  Lock,
  Rocket,
  Target,
  Sparkles,
  ChevronDown,
  Clock,
  Trophy,
  Archive,
  History,
  Award,
  Compass,
  MapPin,
  Layers,
  Trash2,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/membros/meu-projeto/")({
  head: () => ({
    meta: [
      { title: "Meu Projeto — Projeto 101" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: MyProjectPage,
});

function MyProjectPage() {
  const navigate = useNavigate();
  const fetchActive = useServerFn(getActiveProject);
  const runComplete = useServerFn(completeTask);
  const runUncomplete = useServerFn(uncompleteTask);
  const fetchProjects = useServerFn(getUserProjects);
  const fetchAchievements = useServerFn(listAchievements);
  const fetchWorkspace = useServerFn(getBusinessWorkspace);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["active-project"],
    queryFn: () => fetchActive(),
  });
  const { data: projects } = useQuery({
    queryKey: ["user-projects"],
    queryFn: () => fetchProjects(),
  });
  const { data: achievements } = useQuery({
    queryKey: ["achievements"],
    queryFn: () => fetchAchievements(),
  });
  const { data: bw } = useQuery({
    queryKey: ["business-workspace"],
    queryFn: () => fetchWorkspace(),
  });

  const toggleTask = useMutation({
    mutationFn: async (v: { taskId: string; completed: boolean }) =>
      v.completed
        ? runUncomplete({ data: { taskId: v.taskId } })
        : runComplete({ data: { taskId: v.taskId } }),
    onSuccess: (res: any, vars) => {
      qc.invalidateQueries({ queryKey: ["active-project"] });
      qc.invalidateQueries({ queryKey: ["user-projects"] });
      qc.invalidateQueries({ queryKey: ["achievements"] });
      qc.invalidateQueries({ queryKey: ["business-workspace"] });
      if (!vars.completed) {
        if (res?.status === "completed") toast.success("🏆 Parabéns! Projeto concluído!");
        else toast.success("Tarefa concluída");
        (res?.unlocked ?? []).forEach((a: any) => {
          toast(`${a.icon} Conquista: ${a.title}`, { duration: 5000 });
        });
      }
    },
    onError: (e: any) => toast.error(e?.message ?? "Não foi possível atualizar a tarefa."),
  });

  const derived = useMemo(() => {
    if (!data) return null;
    const { stages, missions, tasks, taskProgress, missionProgress, missionState } = data as any;
    const doneTaskIds = new Set<string>(
      (taskProgress ?? []).filter((t: any) => t.completed_at).map((t: any) => t.task_id as string),
    );
    const missionProgressMap = new Map<string, any>(
      (missionProgress ?? []).map((m: any) => [m.mission_id, m]),
    );
    const missionStateMap = new Map<string, MissionStateRecord>(
      (missionState ?? []).map((s: any) => [s.mission_id, s]),
    );

    const stageBlocks = (stages ?? []).map((s: any) => {
      const stageMissions = (missions ?? [])
        .filter((m: any) => m.stage_id === s.id)
        .map((m: any) => {
          const mTasks = (tasks ?? []).filter((t: any) => t.mission_id === m.id);
          const doneCount = mTasks.filter((t: any) => doneTaskIds.has(t.id)).length;
          const mp = missionProgressMap.get(m.id);
          const state: MissionState = deriveMissionState(m, missionProgressMap, missionStateMap);
          return {
            ...m,
            taskCount: mTasks.length,
            doneCount,
            pct: Number(mp?.progress_pct) || (state === "completed" ? 100 : 0),
            completed: state === "completed",
            locked: state === "locked",
            executable: isExecutable(m),
            state,
            tasks: mTasks,
          };
        });
      const stageStats = computeStageProgress(stageMissions, missionProgressMap);
      return {
        ...s,
        missions: stageMissions,
        pct: stageStats.pct,
        completed: stageStats.completed,
        executableTotal: stageStats.total,
        executableDone: stageStats.done,
      };
    });

    const allMissions = stageBlocks.flatMap((s: any) => s.missions);
    const projectStats = computeProjectProgress(allMissions, missionProgressMap);
    const resume = resolveResumeMission(stageBlocks as any, missionStateMap);
    const resumeMission = resume
      ? allMissions.find((m: any) => m.id === resume.id) ?? null
      : null;

    return {
      stageBlocks,
      doneTaskIds,
      nextMission: resumeMission,
      resumeReason: resumeMission?.state ?? null,
      projectStats,
      missionStateMap,
    };
  }, [data]);

  const hasProject = !!data;
  useEffect(() => {
    if (hasProject) track("project_home_viewed");
  }, [hasProject]);

  if (isLoading) return <SkeletonHome />;
  if (!data) return <EmptyProjectState />;

  const { project } = data;
  const business = (project as any).business;
  const workspace = (bw as any)?.workspace ?? null;

  const filledBlocks = BLOCKS.filter((b) => hasBlockContent(workspace?.[b.key]));
  const structuringPct = Math.round((filledBlocks.length / BLOCKS.length) * 100);

  const journeyPct = derived?.projectStats.pct ?? 0;
  const totalMissions = derived?.projectStats.total ?? 0;
  const doneMissions = derived?.projectStats.done ?? 0;
  const isCompleted =
    project.status === "completed" || (totalMissions > 0 && doneMissions === totalMissions);

  const nextMission = derived?.nextMission ?? null;
  const currentStage =
    (nextMission
      ? derived?.stageBlocks.find((s: any) => s.id === nextMission.stage_id)
      : null) ??
    derived?.stageBlocks.find((s: any) => !s.completed) ??
    derived?.stageBlocks[derived.stageBlocks.length - 1];

  const stageAlmostDone =
    !!currentStage &&
    currentStage.executableTotal > 0 &&
    currentStage.executableTotal - currentStage.executableDone === 1 &&
    !currentStage.completed;

  const hasStarted = doneMissions > 0 || filledBlocks.length > 0;
  const heroMessage = contextualMessage({
    structuringPct,
    stageTitle: currentStage?.title ?? null,
    stageAlmostDone,
    isCompleted,
    hasStarted,
  });

  const nextBlockKey: BlockKey | null =
    ((nextMission?.deliverable as any)?.target_field as BlockKey | undefined) ??
    ((nextMission?.target_field as BlockKey | undefined) ?? null);

  const openNextMission = (source: "hero" | "next_decision") => {
    if (!nextMission) return;
    track("primary_cta_clicked", {
      metadata: { missionId: nextMission.id, source, mode: derived?.resumeReason },
    });
    navigate({ to: "/membros/meu-projeto/missao/$id", params: { id: nextMission.id }, search: {} });
  };

  return (
    <div className="space-y-8">
      <HeroSection
        business={business}
        stage={currentStage}
        structuringPct={structuringPct}
        journeyPct={journeyPct}
        message={heroMessage}
        isCompleted={isCompleted}
        cta={
          !isCompleted && nextMission
            ? {
                label:
                  derived?.resumeReason === "in_progress"
                    ? "Continuar minha construção"
                    : "Construir a próxima parte",
                onClick: () => openNextMission("hero"),
              }
            : null
        }
      />

      {!isCompleted && nextMission && (
        <NextDecisionCard
          mission={nextMission}
          blockKey={nextBlockKey}
          resumeMode={derived?.resumeReason === "in_progress"}
          onClick={() => openNextMission("next_decision")}
        />
      )}

      {isCompleted && (
        <section className="rounded-2xl border border-[var(--gold)]/30 bg-gradient-to-br from-[var(--gold)]/10 to-white p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[var(--gold)]/20 text-[var(--gold)]">
              <Trophy size={22} />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="font-display text-lg font-extrabold text-[var(--navy)]">
                Você estruturou seu negócio
              </h2>
              <p className="mt-1 text-sm text-[var(--gray-ink)]">
                Todas as decisões desta jornada foram tomadas. Revise seu plano ou
                comece a executar seus primeiros passos no mercado.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link
                  to="/membros/meu-negocio"
                  className="inline-flex items-center gap-2 rounded-lg bg-[var(--navy)] px-4 py-2 text-xs font-semibold text-white"
                >
                  Revisar meu negócio <ArrowRight size={12} />
                </Link>
                <Link
                  to="/membros/negocios"
                  className="inline-flex items-center gap-2 rounded-lg border border-black/10 bg-white px-4 py-2 text-xs font-semibold text-[var(--navy)]"
                >
                  Explorar outros negócios
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      <WhatBuiltSection workspace={workspace} />

      <DeliverablesSummary workspace={workspace} />

      <ConstructionMap stageBlocks={derived?.stageBlocks ?? []} />

      <DetailedTimeline
        stageBlocks={derived?.stageBlocks ?? []}
        doneTaskIds={derived?.doneTaskIds ?? new Set()}
        onToggleTask={(taskId, completed) => toggleTask.mutate({ taskId, completed })}
      />

      <MissionsInPreparation stageBlocks={derived?.stageBlocks ?? []} />

      {achievements && achievements.length > 0 && (
        <AchievementsBar achievements={achievements} />
      )}

      {projects && projects.length > 0 && (
        <ProjectsHistorySection projects={projects} currentId={project.id} />
      )}
    </div>
  );
}

// ---------- Copy contextual (determinística) ----------
function contextualMessage(o: {
  structuringPct: number;
  stageTitle: string | null;
  stageAlmostDone: boolean;
  isCompleted: boolean;
  hasStarted: boolean;
}): string {
  if (o.isCompleted)
    return "Seu negócio está estruturado. Revise as decisões ou parta para a execução no mercado.";
  if (!o.hasStarted) return "Vamos construir a base do seu negócio, uma decisão de cada vez.";
  if (o.stageAlmostDone && o.stageTitle)
    return `Falta uma decisão para concluir a fase ${o.stageTitle}.`;
  if (o.structuringPct >= 75)
    return "Seu negócio já tem quase toda a estrutura definida — vamos fechar os últimos pilares.";
  if (o.structuringPct >= 40)
    return "Seu negócio está ganhando forma. Vamos para a próxima decisão.";
  return "Você já começou a definir as bases do seu negócio. Continue construindo.";
}

// ---------- Hero ----------
function HeroSection({
  business,
  stage,
  structuringPct,
  journeyPct,
  message,
  isCompleted,
  cta,
}: {
  business: any;
  stage: any;
  structuringPct: number;
  journeyPct: number;
  message: string;
  isCompleted: boolean;
  cta: { label: string; onClick: () => void } | null;
}) {
  return (
    <section className="relative overflow-hidden rounded-3xl border border-black/5 bg-gradient-to-br from-white via-white to-[var(--green)]/[0.06] p-6 shadow-sm lg:p-10">
      <div aria-hidden className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-[var(--green)]/10 blur-3xl" />
      <div className="relative grid gap-6 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
        <div className="min-w-0">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${
              isCompleted
                ? "border-[var(--gold)]/30 bg-[var(--gold)]/10 text-[var(--gold)]"
                : "border-[var(--green)]/20 bg-[var(--green)]/[0.06] text-[var(--green)]"
            }`}
          >
            {isCompleted ? <Trophy size={12} /> : <Compass size={12} />}
            {isCompleted
              ? "Negócio estruturado"
              : stage
                ? `Fase atual: ${stage.title}`
                : "Meu Projeto"}
          </span>
          <h1 className="mt-3 font-display text-3xl font-extrabold text-[var(--navy)] lg:text-4xl">
            {business?.title}
          </h1>
          {business?.category?.name && (
            <p className="mt-1 text-sm text-[var(--gray-ink)]">{business.category.name}</p>
          )}
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-[var(--navy)]/85">
            {message}
          </p>
          {cta && (
            <button
              onClick={cta.onClick}
              className="group mt-6 inline-flex items-center gap-2 rounded-xl bg-[var(--navy)] px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-[var(--navy)]/20 transition hover:-translate-y-0.5"
            >
              {cta.label}
              <ArrowRight size={16} className="transition group-hover:translate-x-0.5" />
            </button>
          )}
        </div>

        <div className="w-full rounded-2xl border border-black/5 bg-white/70 p-4 backdrop-blur lg:w-[280px]">
          <ProgressMeter
            label="Estruturação do negócio"
            pct={structuringPct}
            tone="green"
            hint="Blocos definidos no seu plano"
          />
          <div className="mt-4 border-t border-black/5 pt-4">
            <ProgressMeter
              label="Progresso da jornada"
              pct={journeyPct}
              tone="navy"
              hint="Decisões concluídas"
              compact
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function ProgressMeter({
  label,
  pct,
  tone,
  hint,
  compact,
}: {
  label: string;
  pct: number;
  tone: "green" | "navy";
  hint?: string;
  compact?: boolean;
}) {
  const barColor = tone === "green" ? "bg-[var(--green)]" : "bg-[var(--navy)]";
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">{label}</p>
      <div className="mt-1 flex items-baseline gap-1">
        <span className={`font-display font-extrabold text-[var(--navy)] ${compact ? "text-xl" : "text-3xl"}`}>
          {pct}%
        </span>
        {hint && !compact && <span className="text-xs text-[var(--gray-ink)]">definido</span>}
      </div>
      <div className={`mt-2 overflow-hidden rounded-full bg-[var(--gray-soft)] ${compact ? "h-1.5" : "h-2"}`}>
        <div className={`h-full rounded-full ${barColor}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      {hint && compact && <p className="mt-1 text-[10px] text-[var(--gray-ink)]">{hint}</p>}
    </div>
  );
}

// ---------- Próxima decisão ----------
function NextDecisionCard({
  mission,
  blockKey,
  resumeMode,
  onClick,
}: {
  mission: any;
  blockKey: BlockKey | null;
  resumeMode: boolean;
  onClick: () => void;
}) {
  const block = blockKey ? BLOCK_BY_KEY[blockKey] : null;
  const outputs = block ? block.fields.filter((f) => f.format !== "long").slice(0, 4) : [];
  const why = mission.objective || mission.expected_result || null;

  return (
    <section className="rounded-3xl border border-[var(--navy)]/15 bg-gradient-to-br from-[var(--navy)]/[0.04] to-white p-6 shadow-sm lg:p-8">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--navy)]/70">
        <MapPin size={12} />
        {resumeMode ? "Continue de onde parou" : "Próxima decisão"}
      </div>
      <h2 className="mt-2 font-display text-2xl font-extrabold text-[var(--navy)] lg:text-3xl">
        {mission.title}
      </h2>
      {mission.description && (
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[var(--gray-ink)]">
          {mission.description}
        </p>
      )}

      <div className="mt-5 grid gap-3 md:grid-cols-2">
        {why && (
          <div className="rounded-2xl border border-black/5 bg-white p-4">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
              Por que isso importa
            </p>
            <p className="mt-1.5 text-sm leading-relaxed text-[var(--navy)]">{why}</p>
          </div>
        )}
        {block && outputs.length > 0 && (
          <div className="rounded-2xl border border-black/5 bg-white p-4">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
              Ao terminar, você terá
            </p>
            <ul className="mt-1.5 space-y-1 text-sm text-[var(--navy)]">
              {outputs.map((f) => (
                <li key={f.key} className="flex items-center gap-1.5">
                  <CheckCircle2 size={12} className="shrink-0 text-[var(--green)]" />
                  {f.label}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button
          onClick={onClick}
          className="group inline-flex items-center gap-2 rounded-xl bg-[var(--navy)] px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-[var(--navy)]/20 transition hover:-translate-y-0.5"
        >
          {resumeMode ? "Continuar construindo" : "Construir esta parte"}
          <ArrowRight size={16} className="transition group-hover:translate-x-0.5" />
        </button>
        {mission.estimated_minutes ? (
          <span className="inline-flex items-center gap-1 text-xs text-[var(--gray-ink)]">
            <Clock size={12} /> Tempo estimado: {mission.estimated_minutes} min
          </span>
        ) : null}
      </div>
    </section>
  );
}

// ---------- O que você já construiu ----------
function blockStatus(
  workspace: any,
  key: BlockKey,
): { status: "done" | "wip" | "pending"; label: string; summary: string } {
  const v = workspace?.[key];
  if (!hasBlockContent(v))
    return { status: "pending", label: "Ainda não iniciado", summary: "Aguardando esta decisão." };
  const rows = blockFieldRows(BLOCK_BY_KEY[key], v);
  const short = rows.find((r) => !r.long)?.value ?? rows[0]?.value ?? "";
  const done = !!(v && typeof v === "object" && (v as any)._finalized_at);
  return {
    status: done ? "done" : "wip",
    label: done ? "Concluído" : "Em construção",
    summary: short,
  };
}

function WhatBuiltSection({ workspace }: { workspace: any }) {
  const filled = BLOCKS.filter((b) => hasBlockContent(workspace?.[b.key]));
  const rest = BLOCKS.filter((b) => !hasBlockContent(workspace?.[b.key]));
  const shown = [...filled, ...rest].slice(0, 4);

  return (
    <section>
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-extrabold text-[var(--navy)]">
            O que você já construiu
          </h2>
          <p className="text-xs text-[var(--gray-ink)]">
            Cada decisão vira uma parte real do seu negócio.
          </p>
        </div>
        <Link
          to="/membros/meu-negocio"
          className="shrink-0 text-xs font-semibold text-[var(--navy)] hover:underline"
        >
          Ver plano completo →
        </Link>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {shown.map((b) => {
          const s = blockStatus(workspace, b.key);
          const Icon = b.icon;
          return (
            <div
              key={b.key}
              className={`flex flex-col rounded-2xl border p-4 ${
                s.status === "done"
                  ? "border-[var(--green)]/25 bg-[var(--green)]/[0.04]"
                  : s.status === "wip"
                    ? "border-[var(--navy)]/15 bg-white shadow-sm"
                    : "border-dashed border-black/10 bg-white/60"
              }`}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${
                    s.status === "done"
                      ? "bg-[var(--green)]/15 text-[var(--green)]"
                      : s.status === "wip"
                        ? "bg-[var(--navy)]/10 text-[var(--navy)]"
                        : "bg-[var(--gray-soft)] text-[var(--gray-ink)]"
                  }`}
                >
                  <Icon size={13} />
                </span>
                <p className="min-w-0 truncate font-display text-sm font-extrabold text-[var(--navy)]">
                  {b.title}
                </p>
              </div>
              <p
                className={`mt-2 text-[10px] font-semibold uppercase tracking-wide ${
                  s.status === "done"
                    ? "text-[var(--green)]"
                    : s.status === "wip"
                      ? "text-[var(--navy)]/70"
                      : "text-[var(--gray-ink)]"
                }`}
              >
                {s.label}
              </p>
              {s.summary && (
                <p className="mt-1.5 line-clamp-2 flex-1 text-xs text-[var(--gray-ink)]">
                  {s.summary}
                </p>
              )}
              {s.status !== "pending" && (
                <Link
                  to="/membros/meu-negocio"
                  onClick={() =>
                    track("business_block_reviewed", { metadata: { block: b.key } })
                  }
                  className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-[var(--navy)] hover:underline"
                >
                  Revisar <ArrowRight size={10} />
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ---------- Mapa da construção ----------
function ConstructionMap({ stageBlocks }: { stageBlocks: any[] }) {
  if (stageBlocks.length === 0) return null;
  return (
    <section>
      <div className="mb-4">
        <h2 className="font-display text-xl font-extrabold text-[var(--navy)]">
          Mapa da construção
        </h2>
        <p className="text-xs text-[var(--gray-ink)]">
          Cada fase representa um pilar do seu negócio.
        </p>
      </div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {stageBlocks.map((s: any, idx: number) => {
          const nextInStage =
            s.missions.find((m: any) => m.state === "in_progress") ??
            s.missions.find((m: any) => m.state === "available");
          const stageState: "done" | "wip" | "pending" = s.completed
            ? "done"
            : s.pct > 0 || nextInStage?.state === "in_progress"
              ? "wip"
              : "pending";
          return (
            <div
              key={s.id}
              className={`rounded-2xl border p-4 ${
                stageState === "done"
                  ? "border-[var(--green)]/25 bg-[var(--green)]/[0.04]"
                  : stageState === "wip"
                    ? "border-[var(--navy)]/20 bg-white shadow-sm"
                    : "border-black/5 bg-white/70"
              }`}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl font-display text-xs font-extrabold ${
                    stageState === "done"
                      ? "bg-[var(--green)] text-white"
                      : stageState === "wip"
                        ? "bg-[var(--navy)] text-white"
                        : "bg-[var(--gray-soft)] text-[var(--gray-ink)]"
                  }`}
                >
                  {stageState === "done" ? <CheckCircle2 size={14} /> : idx + 1}
                </span>
                <p className="min-w-0 flex-1 truncate font-display text-sm font-extrabold text-[var(--navy)]">
                  {s.title}
                </p>
              </div>

              <div className="mt-3 flex items-center gap-2">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[var(--gray-soft)]">
                  <div
                    className="h-full rounded-full bg-[var(--green)]"
                    style={{ width: `${s.pct}%` }}
                  />
                </div>
                <span className="text-[10px] font-semibold text-[var(--gray-ink)]">{s.pct}%</span>
              </div>
              <p className="mt-2 text-[11px] text-[var(--gray-ink)]">
                <strong className="text-[var(--navy)]">{s.executableDone}</strong>/
                {s.executableTotal} decisões
              </p>

              {stageState !== "done" && nextInStage && (
                <Link
                  to="/membros/meu-projeto/missao/$id"
                  params={{ id: nextInStage.id }}
                  search={{}}
                  className="mt-3 block truncate text-[11px] font-semibold text-[var(--navy)] hover:underline"
                  title={nextInStage.title}
                >
                  → {nextInStage.title}
                </Link>
              )}
              {stageState === "done" && (
                <p className="mt-3 text-[11px] font-semibold text-[var(--green)]">Fase concluída</p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ---------- Cronograma detalhado (secundário, colapsado) ----------
function DetailedTimeline({
  stageBlocks,
  doneTaskIds,
  onToggleTask,
}: {
  stageBlocks: any[];
  doneTaskIds: Set<string>;
  onToggleTask: (taskId: string, completed: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <section>
      <details
        open={open}
        onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
        className="rounded-2xl border border-black/5 bg-white"
      >
        <summary className="flex cursor-pointer items-center gap-3 p-5 [&::-webkit-details-marker]:hidden">
          <Layers size={18} className="shrink-0 text-[var(--gray-ink)]" />
          <div className="flex-1">
            <p className="font-display text-base font-extrabold text-[var(--navy)]">
              Cronograma detalhado
            </p>
            <p className="text-xs text-[var(--gray-ink)]">
              Veja cada decisão desta jornada e o estado atual.
            </p>
          </div>
          <ChevronDown
            size={16}
            className={`shrink-0 text-[var(--gray-ink)] transition ${open ? "rotate-180" : ""}`}
          />
        </summary>
        <div className="space-y-5 border-t border-black/5 p-5">
          {stageBlocks.map((s: any, idx: number) => (
            <div key={s.id}>
              <div className="mb-2 flex items-center gap-2">
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md font-display text-[10px] font-extrabold ${
                    s.completed ? "bg-[var(--green)] text-white" : "bg-[var(--gray-soft)] text-[var(--gray-ink)]"
                  }`}
                >
                  {idx + 1}
                </span>
                <p className="min-w-0 truncate font-display text-sm font-extrabold text-[var(--navy)]">
                  {s.title}
                </p>
                <span className="ml-auto text-[10px] font-semibold text-[var(--gray-ink)]">
                  {s.pct}%
                </span>
              </div>
              <div className="space-y-2">
                {s.missions
                  .filter((m: any) => m.state !== "unavailable")
                  .map((m: any) => (
                    <MissionRow
                      key={m.id}
                      mission={m}
                      doneTaskIds={doneTaskIds}
                      onToggleTask={onToggleTask}
                    />
                  ))}
              </div>
            </div>
          ))}
        </div>
      </details>
    </section>
  );
}

// ---------- Missões em preparação ----------
function MissionsInPreparation({ stageBlocks }: { stageBlocks: any[] }) {
  const all = stageBlocks.flatMap((s: any) =>
    s.missions
      .filter((m: any) => m.state === "unavailable")
      .map((m: any) => ({ id: m.id, title: m.title, stage: s.title })),
  );
  if (all.length === 0) return null;
  return (
    <section>
      <details className="rounded-2xl border border-dashed border-black/10 bg-white/60">
        <summary className="flex cursor-pointer items-center gap-2 p-4 text-xs font-semibold text-[var(--gray-ink)] [&::-webkit-details-marker]:hidden">
          <ChevronDown size={12} /> Próximas construções em preparação ({all.length})
        </summary>
        <ul className="space-y-1.5 border-t border-black/5 p-4 text-xs text-[var(--gray-ink)]">
          {all.map((m) => (
            <li key={m.id} className="flex items-center gap-2">
              <span className="shrink-0 rounded-md border border-black/10 bg-white px-2 py-0.5 text-[10px] uppercase tracking-wide">
                Em preparação
              </span>
              <span className="truncate">
                {m.stage} · {m.title}
              </span>
            </li>
          ))}
        </ul>
      </details>
    </section>
  );
}

// ---------- Conquistas ----------
function AchievementsBar({ achievements }: { achievements: any[] }) {
  return (
    <section className="rounded-2xl border border-black/5 bg-white p-5">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-extrabold text-[var(--navy)]">Conquistas</h2>
          <p className="text-xs text-[var(--gray-ink)]">
            {achievements.length} conquista{achievements.length === 1 ? "" : "s"} desbloqueada
            {achievements.length === 1 ? "" : "s"}
          </p>
        </div>
        <Award size={18} className="text-[var(--gold)]" />
      </div>
      <div className="flex flex-wrap gap-2">
        {achievements.slice(0, 12).map((a: any) => (
          <div
            key={a.id}
            title={a.description}
            className="flex items-center gap-2 rounded-full border border-[var(--gold)]/20 bg-[var(--gold)]/[0.06] px-3 py-1.5 text-xs font-semibold text-[var(--navy)]"
          >
            <span className="text-base leading-none">{a.icon}</span>
            {a.title}
          </div>
        ))}
      </div>
    </section>
  );
}

function SkeletonHome() {
  return (
    <div className="animate-pulse space-y-6">
      <div className="h-56 rounded-3xl bg-black/5" />
      <div className="h-40 rounded-2xl bg-black/5" />
      <div className="h-32 rounded-2xl bg-black/5" />
      <div className="h-48 rounded-2xl bg-black/5" />
    </div>
  );
}



function MissionRow({
  mission,
  doneTaskIds,
  onToggleTask,
}: {
  mission: any;
  doneTaskIds: Set<string>;
  onToggleTask: (taskId: string, completed: boolean) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const state: MissionState = mission.state;
  const executable = mission.executable;
  const hasLegacyTasks = mission.tasks && mission.tasks.length > 0;

  // Unavailable (migration/placeholder) — discreet, non-competing row.
  if (state === "unavailable") {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-dashed border-black/10 bg-[var(--gray-soft)]/20 px-4 py-3 text-xs text-[var(--gray-ink)]">
        <div className="flex-1 min-w-0">
          <p className="truncate font-medium">{mission.title}</p>
        </div>
        <span className="shrink-0 rounded-md border border-black/10 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
          Em atualização
        </span>
      </div>
    );
  }

  if (state === "locked") {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-dashed border-black/10 bg-[var(--gray-soft)]/30 p-4 text-sm text-[var(--gray-ink)]">
        <Lock size={16} />
        <div className="flex-1">
          <p className="font-semibold">{mission.title}</p>
          <p className="text-xs">Conclua a missão anterior para desbloquear.</p>
        </div>
      </div>
    );
  }

  const statusLabel =
    state === "completed"
      ? "Concluída"
      : state === "in_progress"
        ? mission.pct > 0
          ? `${Math.round(mission.pct)}% em andamento`
          : "Em construção"
        : "Pronta para começar";

  const primaryLabel =
    state === "completed"
      ? "Revisar"
      : state === "in_progress"
        ? "Continuar construindo"
        : "Construir esta parte";

  return (
    <div className="rounded-xl border border-black/5 bg-white">
      {/* Row is a Link (whole line clickable) with an expand toggle for legacy tasks only. */}
      <div className="flex items-stretch">
        <Link
          to="/membros/meu-projeto/missao/$id"
          params={{ id: mission.id }}
          search={{}}
          className="flex flex-1 items-center gap-3 rounded-l-xl p-4 text-left transition hover:bg-[var(--gray-soft)]/40"
        >
          <span
            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
              state === "completed"
                ? "bg-[var(--green)] text-white"
                : state === "in_progress"
                  ? "bg-[var(--navy)]/10 text-[var(--navy)]"
                  : "border border-black/10 bg-white text-[var(--gray-ink)]"
            }`}
          >
            {state === "completed" ? <CheckCircle2 size={14} /> : <Target size={12} />}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[var(--navy)]">{mission.title}</p>
            <p className="text-xs text-[var(--gray-ink)]">
              {statusLabel}
              {mission.estimated_minutes ? (
                <>
                  {" "}·{" "}
                  <Clock size={10} className="inline" /> {mission.estimated_minutes} min
                </>
              ) : null}
            </p>
          </div>
          <span className="hidden shrink-0 items-center gap-1 rounded-md bg-[var(--navy)] px-2.5 py-1 text-xs font-semibold text-white md:inline-flex">
            {primaryLabel} <ArrowRight size={11} />
          </span>
        </Link>
        {hasLegacyTasks && (
          <button
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? "Recolher tarefas" : "Expandir tarefas"}
            className="border-l border-black/5 px-3 text-[var(--gray-ink)] transition hover:bg-[var(--gray-soft)]/40"
          >
            <ChevronDown size={14} className={`transition ${expanded ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>

      {/* Mobile primary action */}
      <div className="border-t border-black/5 p-3 md:hidden">
        <Link
          to="/membros/meu-projeto/missao/$id"
          params={{ id: mission.id }}
          search={{}}
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-[var(--navy)] px-3 py-2 text-xs font-semibold text-white"
        >
          {primaryLabel} <ArrowRight size={11} />
        </Link>
      </div>

      {hasLegacyTasks && expanded && (
        <div className="border-t border-black/5 p-4">
          <ul className="space-y-1.5">
            {mission.tasks.map((t: any) => {
              const done = doneTaskIds.has(t.id);
              return (
                <li
                  key={t.id}
                  className="flex items-start gap-2.5 rounded-lg px-2 py-1.5 text-sm hover:bg-[var(--gray-soft)]/50"
                >
                  <button
                    onClick={() => onToggleTask(t.id, done)}
                    className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition ${
                      done
                        ? "border-[var(--green)] bg-[var(--green)] text-white"
                        : "border-black/20 bg-white hover:border-[var(--green)]"
                    }`}
                    aria-label={done ? "Desmarcar" : "Marcar como concluída"}
                  >
                    {done && <CheckCircle2 size={12} />}
                  </button>
                  <span
                    className={`flex-1 ${
                      done ? "text-[var(--gray-ink)] line-through" : "text-[var(--navy)]"
                    }`}
                  >
                    {t.title}
                  </span>
                  {executable && t.requires_answer && (
                    <Link
                      to="/membros/meu-projeto/missao/$id"
                      params={{ id: mission.id }}
                      search={{}}
                      className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-[var(--gold)]"
                    >
                      Precisa resposta
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}


function EmptyProjectState() {
  return (
    <div className="mx-auto max-w-2xl rounded-3xl border border-black/5 bg-white p-10 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--green)]/10 text-[var(--green)]">
        <Sparkles size={24} />
      </div>
      <h1 className="mt-5 font-display text-2xl font-extrabold text-[var(--navy)]">
        Você ainda não escolheu seu projeto
      </h1>
      <p className="mt-2 text-sm text-[var(--gray-ink)]">
        Explore os 101 negócios e escolha aquele que deseja construir. A partir daí, a
        plataforma vai organizar um cronograma passo a passo para você executar.
      </p>
      <Link
        to="/membros/negocios"
        className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[var(--navy)] px-5 py-3 text-sm font-semibold text-white hover:bg-[var(--navy)]/90"
      >
        Escolher meu negócio <ArrowRight size={14} />
      </Link>
    </div>
  );
}

function ProjectsHistorySection({
  projects,
  currentId,
}: {
  projects: any[];
  currentId: string;
}) {
  const [tab, setTab] = useState<"active" | "completed" | "archived">("active");
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const qc = useQueryClient();
  const runDelete = useServerFn(deleteProject);
  const del = useMutation({
    mutationFn: (projectId: string) => runDelete({ data: { projectId } }),
    onSuccess: () => {
      toast.success("Projeto excluído.");
      qc.invalidateQueries({ queryKey: ["user-projects"] });
      qc.invalidateQueries({ queryKey: ["active-project"] });
      qc.invalidateQueries({ queryKey: ["business-workspace"] });
      qc.invalidateQueries({ queryKey: ["achievements"] });
      setConfirmId(null);
    },
    onError: (e: any) => toast.error(e?.message ?? "Não foi possível excluir."),
  });

  const filtered = projects.filter((p) => p.status === tab);
  const counts = {
    active: projects.filter((p) => p.status === "active").length,
    completed: projects.filter((p) => p.status === "completed").length,
    archived: projects.filter((p) => p.status === "archived").length,
  };
  const tabs: Array<{ id: typeof tab; label: string; icon: any }> = [
    { id: "active", label: "Em andamento", icon: Rocket },
    { id: "completed", label: "Concluídos", icon: Trophy },
    { id: "archived", label: "Arquivados", icon: Archive },
  ];

  return (
    <section className="rounded-2xl border border-black/5 bg-white p-5">
      <div className="mb-4 flex items-center gap-2">
        <History size={16} className="text-[var(--gray-ink)]" />
        <h2 className="font-display text-lg font-extrabold text-[var(--navy)]">
          Meus projetos
        </h2>
      </div>
      <div className="mb-4 flex flex-wrap gap-1.5 border-b border-black/5">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-semibold transition ${
                active
                  ? "border-[var(--navy)] text-[var(--navy)]"
                  : "border-transparent text-[var(--gray-ink)] hover:text-[var(--navy)]"
              }`}
            >
              <Icon size={12} /> {t.label}
              <span className="rounded-full bg-[var(--gray-soft)] px-1.5 text-[10px] text-[var(--gray-ink)]">
                {counts[t.id]}
              </span>
            </button>
          );
        })}
      </div>
      {filtered.length === 0 ? (
        <p className="py-6 text-center text-sm text-[var(--gray-ink)]">
          Nenhum projeto nesta lista ainda.
        </p>
      ) : (
        <ul className="divide-y divide-black/5">
          {filtered.map((p) => {
            const isCurrent = p.id === currentId;
            const isConfirming = confirmId === p.id;
            return (
              <li key={p.id} className="flex flex-wrap items-center gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-[var(--navy)]">
                    {p.business?.title ?? "Sem título"}
                    {isCurrent && (
                      <span className="ml-2 rounded-full bg-[var(--green)]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--green)]">
                        Atual
                      </span>
                    )}
                  </p>
                  <p className="mt-0.5 text-xs text-[var(--gray-ink)]">
                    {p.business?.category?.name ?? "—"} ·{" "}
                    {Number(p.progress_pct).toFixed(0)}% · início{" "}
                    {new Date(p.started_at).toLocaleDateString("pt-BR")}
                  </p>
                  {isConfirming && (
                    <p className="mt-1.5 text-[11px] text-red-700">
                      Excluir apaga o workspace, decisões, tarefas e conquistas deste projeto. Ação irreversível.
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  {isCurrent ? (
                    <Link
                      to="/membros/meu-projeto"
                      className="rounded-lg border border-black/10 px-3 py-1.5 text-xs font-semibold text-[var(--navy)] hover:bg-[var(--gray-soft)]"
                    >
                      Ver
                    </Link>
                  ) : (
                    <Link
                      to="/membros/meu-projeto/projeto/$id"
                      params={{ id: p.id }}
                      className="rounded-lg border border-black/10 px-3 py-1.5 text-xs font-semibold text-[var(--navy)] hover:bg-[var(--gray-soft)]"
                    >
                      Abrir histórico
                    </Link>
                  )}
                  {isConfirming ? (
                    <>
                      <button
                        onClick={() => del.mutate(p.id)}
                        disabled={del.isPending}
                        className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                      >
                        {del.isPending ? "Excluindo…" : "Confirmar exclusão"}
                      </button>
                      <button
                        onClick={() => setConfirmId(null)}
                        disabled={del.isPending}
                        className="rounded-lg border border-black/10 px-3 py-1.5 text-xs font-semibold text-[var(--gray-ink)] hover:bg-[var(--gray-soft)]"
                      >
                        Cancelar
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setConfirmId(p.id)}
                      className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-2.5 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
                      title="Excluir projeto"
                    >
                      <Trash2 size={12} /> Excluir
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// GS.4 — Resumo de "Materiais construídos" na home do projeto.
// Só aparece com a flag hamburgueria_gold_standard. Não compete com a
// próxima decisão; é apenas um atalho para a Central de Entregáveis.
function DeliverablesSummary({ workspace }: { workspace: any }) {
  const enabled = useFeatureFlag("hamburgueria_gold_standard");
  if (!enabled) return null;
  const items = summarizeDeliverables(workspace);
  if (items.length === 0) return null;
  return (
    <section className="rounded-2xl border border-black/5 bg-white p-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
            Materiais construídos
          </p>
          <h2 className="mt-0.5 font-display text-base font-extrabold text-[var(--navy)]">
            Ativos gerados pelas suas decisões
          </h2>
        </div>
        <Link
          to="/membros/meu-projeto/materiais"
          className="inline-flex items-center gap-1 rounded-md border border-black/10 px-3 py-1.5 text-xs font-semibold text-[var(--navy)] hover:bg-[var(--gray-soft)]"
        >
          Abrir central <ArrowRight size={12} />
        </Link>
      </div>
      <ul className="mt-3 grid gap-2 sm:grid-cols-2">
        {items.map((it) => {
          const s = STATUS_STYLES[it.status];
          return (
            <li key={it.id} className={`flex items-center justify-between gap-2 rounded-lg border ${s.ring} bg-white px-3 py-2`}>
              <span className="text-sm font-semibold text-[var(--navy)]">{it.title}</span>
              <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${s.chip}`}>{it.statusLabel}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
